import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import './DatePicker.css';

const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isDateOutOfRange = (date, minDate, maxDate) => {
  const day = startOfDay(date).getTime();
  if (minDate && day < startOfDay(minDate).getTime()) return true;
  if (maxDate && day > startOfDay(maxDate).getTime()) return true;
  return false;
};

const DatePicker = ({ value, onChange, className = '', minDate = '', maxDate = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedDate = parseLocalDate(value);
  const minimumDate = parseLocalDate(minDate);
  const maximumDate = parseLocalDate(maxDate);

  const DROPDOWN_WIDTH = 280;
  const PADDING = 16;

  const updateDropdownPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const maxLeft = window.innerWidth - DROPDOWN_WIDTH - PADDING;
      const minLeft = PADDING;
      let left = rect.left;
      if (left < minLeft) left = minLeft;
      if (left > maxLeft) left = maxLeft;
      const maxTop = window.innerHeight - 320 - PADDING;
      let top = rect.bottom + 8;
      if (top > maxTop) top = Math.max(PADDING, rect.top - 320 - 8);
      setDropdownPosition({ top, left });
    }
  };

  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    }
  }, [value]);

  useLayoutEffect(() => {
    if (isOpen && containerRef.current) {
      updateDropdownPosition();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inContainer = containerRef.current && containerRef.current.contains(event.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      if (!inContainer && !inDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const parsed = parseLocalDate(dateStr);
    if (!parsed) return '';
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatOutputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false,
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }

    return days;
  };

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToPrevYear = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth(), 1));
  };

  const goToNextYear = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth(), 1));
  };

  const handleSelectDate = (date) => {
    if (isDateOutOfRange(date, minimumDate, maximumDate)) return;
    onChange(formatOutputDate(date));
    setIsOpen(false);
  };

  const today = new Date();
  const isTodayOutOfRange = isDateOutOfRange(today, minimumDate, maximumDate);

  const handleToday = () => {
    if (isTodayOutOfRange) return;
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    onChange(formatOutputDate(today));
    setIsOpen(false);
  };

  const isSelected = (date) => {
    if (!selectedDate) return false;
    return (
      date.getFullYear() === selectedDate.getFullYear() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getDate() === selectedDate.getDate()
    );
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];
  const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];

  return (
    <div ref={containerRef} className={`date-picker ${className}`}>
      <div className="date-picker__input-wrapper" onClick={() => setIsOpen(!isOpen)}>
        <input
          type="text"
          className="date-picker__display"
          value={formatDisplayDate(value)}
          readOnly
          placeholder="yyyy-mm-dd"
        />
        <span className="date-picker__icon">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path
              fill="currentColor"
              d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"
            />
          </svg>
        </span>
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="date-picker__dropdown date-picker__dropdown--portal"
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 1050,
            }}
          >
            <div className="date-picker__header">
            <button type="button" className="date-picker__nav-btn" onClick={goToPrevYear}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5">
  <path fillRule="evenodd" d="M4.72 9.47a.75.75 0 0 0 0 1.06l4.25 4.25a.75.75 0 1 0 1.06-1.06L6.31 10l3.72-3.72a.75.75 0 1 0-1.06-1.06L4.72 9.47Zm9.25-4.25L9.72 9.47a.75.75 0 0 0 0 1.06l4.25 4.25a.75.75 0 1 0 1.06-1.06L11.31 10l3.72-3.72a.75.75 0 0 0-1.06-1.06Z" clipRule="evenodd" />
</svg>

            </button>
            <button type="button" className="date-picker__nav-btn" onClick={goToPrevMonth}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-5">
            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
            </svg>

            </button>
            <span className="date-picker__current-month">
              {currentMonth.getFullYear()}年 {monthNames[currentMonth.getMonth()]}
            </span>
            <button type="button" className="date-picker__nav-btn" onClick={goToNextMonth}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5">
  <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
</svg>

            </button>
            <button type="button" className="date-picker__nav-btn" onClick={goToNextYear}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5">
  <path fillRule="evenodd" d="M15.28 9.47a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 1 1-1.06-1.06L13.69 10 9.97 6.28a.75.75 0 0 1 1.06-1.06l4.25 4.25ZM6.03 5.22l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L8.69 10 4.97 6.28a.75.75 0 0 1 1.06-1.06Z" clipRule="evenodd" />
</svg>

            </button>
          </div>
          <div className="date-picker__weekdays">
            {weekDays.map((day, index) => (
              <div
                key={day}
                className={`date-picker__weekday ${
                  index === 0 ? 'date-picker__weekday--sunday' : ''
                } ${index === 6 ? 'date-picker__weekday--saturday' : ''}`}
              >
                {day}
              </div>
            ))}
          </div>
          <div className="date-picker__days">
            {getDaysInMonth(currentMonth).map((dayObj, index) => {
              const isDisabled = isDateOutOfRange(dayObj.date, minimumDate, maximumDate);
              return (
              <button
                key={index}
                type="button"
                disabled={isDisabled}
                className={`date-picker__day ${
                  !dayObj.isCurrentMonth ? 'date-picker__day--other-month' : ''
                } ${isSelected(dayObj.date) ? 'date-picker__day--selected' : ''} ${
                  isToday(dayObj.date) ? 'date-picker__day--today' : ''
                } ${dayObj.date.getDay() === 0 ? 'date-picker__day--sunday' : ''} ${
                  dayObj.date.getDay() === 6 ? 'date-picker__day--saturday' : ''
                } ${isDisabled ? 'date-picker__day--disabled' : ''}`}
                onClick={() => handleSelectDate(dayObj.date)}
              >
                {dayObj.date.getDate()}
              </button>
              );
            })}
          </div>
          <div className="date-picker__footer">
            <button
              type="button"
              className="date-picker__today-btn"
              onClick={handleToday}
              disabled={isTodayOutOfRange}
            >
              今日
            </button>
          </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DatePicker;
